import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Badge,
  Button,
  Empty,
  Flex,
  List,
  Popover,
  Spin,
  Typography,
  notification as antdNotification,
} from "antd";
import { Icon } from "@iconify/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import pallete from "../utils/pallete";
import { useMe } from "../hooks/useMe";
import { useNotificationStream } from "../hooks/useNotificationStream";
import { notificationService } from "../services/notification.service";
import { canReceiveNotifications } from "../utils/notificationAccess";

const LIST_LIMIT = 10;
// Fallback if the real-time stream is unavailable (e.g. blocked by a proxy).
const POLL_INTERVAL_MS = 60000;

export default function NotificationBell() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [toast, toastContextHolder] = antdNotification.useNotification();

  const { data: me } = useMe();
  const enabled = canReceiveNotifications(me?.data);

  const { data: unreadData } = useQuery({
    queryKey: ["notifications-unread"],
    queryFn: notificationService.fetchUnreadCount,
    enabled,
    refetchInterval: POLL_INTERVAL_MS,
  });
  const unreadCount = unreadData?.data?.count || 0;

  const { data: listData, isLoading: isListLoading } = useQuery({
    queryKey: ["notifications", { limit: LIST_LIMIT }],
    queryFn: () => notificationService.fetchAll({ limit: LIST_LIMIT }),
    enabled: enabled && open,
  });
  const notifications = listData?.data || [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["notifications"] });
    queryClient.invalidateQueries({ queryKey: ["notifications-unread"] });
  };

  const { mutate: markRead } = useMutation({
    mutationFn: notificationService.markRead,
    onSuccess: refresh,
  });
  const { mutate: markAllRead, isPending: isMarkingAll } = useMutation({
    mutationFn: notificationService.markAllRead,
    onSuccess: refresh,
  });

  const openNotification = (item) => {
    if (!item.isRead) markRead(item.uuid);
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  useNotificationStream({
    enabled,
    onNotification: (item) => {
      const key = item.uuid;
      toast.info({
        key,
        message: item.title,
        description: item.message,
        placement: "topRight",
        icon: (
          <Icon
            icon="solar:bell-bing-bold-duotone"
            color={pallete.primary[500]}
          />
        ),
        onClick: () => {
          toast.destroy(key);
          openNotification(item);
        },
        style: { cursor: item.link ? "pointer" : "default" },
      });
    },
  });

  if (!enabled) return null;

  const content = (
    <div style={{ width: 360, maxWidth: "80vw" }}>
      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
        <Typography.Text strong>Notifikasi</Typography.Text>
        <Button
          type="link"
          size="small"
          style={{ padding: 0 }}
          disabled={unreadCount === 0}
          loading={isMarkingAll}
          onClick={() => markAllRead()}
        >
          Tandai semua dibaca
        </Button>
      </Flex>
      {isListLoading ? (
        <Flex justify="center" style={{ padding: 24 }}>
          <Spin />
        </Flex>
      ) : notifications.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Belum ada notifikasi"
        />
      ) : (
        <List
          style={{ maxHeight: 420, overflowY: "auto" }}
          dataSource={notifications}
          renderItem={(item) => (
            <List.Item
              onClick={() => openNotification(item)}
              style={{
                cursor: "pointer",
                padding: "10px 8px",
                borderRadius: 6,
                backgroundColor: item.isRead
                  ? "transparent"
                  : pallete.primary[50],
              }}
            >
              <Flex vertical gap={2} style={{ width: "100%" }}>
                <Flex justify="space-between" gap={8}>
                  <Typography.Text strong={!item.isRead}>
                    {item.title}
                  </Typography.Text>
                  {!item.isRead && <Badge status="warning" />}
                </Flex>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {item.message}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {dayjs(item.createdAt).format("DD/MM/YYYY HH:mm")}
                </Typography.Text>
              </Flex>
            </List.Item>
          )}
        />
      )}
    </div>
  );

  return (
    <>
      {toastContextHolder}
      <Popover
        content={content}
        trigger="click"
        placement="bottomRight"
        open={open}
        onOpenChange={setOpen}
      >
        <Badge count={unreadCount} size="small" offset={[-4, 4]}>
          <Button
            aria-label="Notifikasi"
            icon={
              <Icon
                icon="solar:bell-bing-line-duotone"
                width={20}
                height={20}
                style={{ marginBottom: -3 }}
              />
            }
          />
        </Badge>
      </Popover>
    </>
  );
}
